// The small graph that gives the context of something an analysis view points at: a role or system with the actions it
// does and the work flowing between them, an action with the steps either side of it, an output with the steps that
// lead to it, or a detour from where items leave the main flow to where they return. Pure, so it can be tested
// without a page; the context dialog draws what it builds
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { ACTION_TYPE } from '../common/organisation';
import { LensKey } from '../lenses/lens';
import { LensGraphEdge, LensGraphNode } from '../lenses/lens-node-style';

export type ContextKind = 'role' | 'system' | 'action' | 'output' | 'detour';

// what to show the context of
export interface ContextSubject {
    kind: ContextKind;
    key: string;                // the node's key; for a detour, where items leave the main flow
    joinKey?: string;           // a detour: where they return
    name?: string;              // to title it before the graph is loaded
}

export interface ContextGraphData {
    nodes: AnalysisNode[];
    edges: AnalysisEdge[];
}

export interface ContextGraph {
    title: string;
    subtitle: string;
    nodes: LensGraphNode[];
    edges: LensGraphEdge[];
    focus: string[];            // the nodes it is about, highlighted
    truncated: number;          // nodes left out to keep it readable
    lens: LensKey;              // the analysis view to drill into
    lensFocus?: string;         // the node that view can show
}

// the most a context shows, so it stays readable; the largest by hours are kept
export const MAX_CONTEXT_NODES = 30;

const FOCUS_FILL = '#fff4d6';
const LINK_COLOUR = '#94a3b8';
const FLOW_COLOUR = '#64748B';
const DETOUR_COLOUR = '#e34948';

const ACTION_STATUS: Record<number, string> = {
    [ACTION_TYPE.AUTOMATED]: 'automated', [ACTION_TYPE.MANUAL]: 'manual', [ACTION_TYPE.BOTH]: 'assisted',
    [ACTION_TYPE.NONE]: 'unassigned', [ACTION_TYPE.EXTERNAL]: 'unknown',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const theSteps = (n: number) => n ? `the ${plural(n, 'step')}` : 'no steps';
const hoursText = (h: number) => { const n = Math.round(h || 0); return `${n.toLocaleString('en')} hour${n === 1 ? '' : 's'} a year`; };
// a share held as a fraction (0.3 is 30%)
const shareText = (share: number) => share >= 0.01 ? `${Math.round(share * 100)}%` : 'under 1%';

const isAction = (n: AnalysisNode) => n.superType === NODE_SUPERTYPE.ACTION;
const roleKeys = (a: AnalysisNode) => (a.roles || []).map(r => r._key);
const systemKeys = (a: AnalysisNode) => [...new Set([...(a.systems || []), ...(a.roles || []).flatMap(r => r.systemKeys || [])])];

interface Builder {
    nodes: Map<string, LensGraphNode>;
    edges: Map<string, LensGraphEdge>;
    passed: Map<string, string[]>;     // the information named on each edge
}

// a node of the analysis graph as a card: its name, what it is and its hours, coloured by its model
const card = (n: AnalysisNode, colours: Map<string, string>, focus: boolean): LensGraphNode => {
    const output = n.superType === NODE_SUPERTYPE.INFORMATION || n.superType === NODE_SUPERTYPE.OUTCOMES;
    const detail = isAction(n) ? `${hoursText(n.hours)}${n.onDetour || n.exceptionWork ? ' · exception work' : ''}`
        : n.superType === NODE_SUPERTYPE.OUTCOMES ? 'Outcome' : `Output · ${Math.round(n.items || 0).toLocaleString('en')} items a year`;
    return {
        key: n._key, modelKey: n.modelKey, kind: output ? 'output' : undefined, label: n.name || 'Unnamed', detail,
        segments: [{ colour: colours.get(n.modelKey) || '#94a3b8', share: 1 }],
        status: isAction(n) ? ACTION_STATUS[n.actionType ?? ACTION_TYPE.NONE] || 'unknown' : 'none',
        dashed: !!(n.onDetour || n.exceptionWork), tooltip: `${n.name}: ${detail}`, fill: focus ? FOCUS_FILL : undefined,
    };
};

// a role or system, which the analysis graph folds into its actions, as a card of its own
const performerCard = (key: string, kind: 'Role' | 'System', name: string, actions: number, focus: boolean): LensGraphNode => ({
    key, modelKey: ORGANISATION_MODEL_KEY, label: name || kind, detail: `${kind} · ${plural(actions, 'action')}`, segments: [{ colour: kind === 'Role' ? '#A55FA3' : '#5E7FA6', share: 1 }],
    status: 'none', dashed: false, tooltip: `${name}: ${kind.toLowerCase()} doing ${plural(actions, 'action')} here`, fill: focus ? FOCUS_FILL : undefined,
});

// the most pieces of information named on an edge before the rest are counted
const MAX_LABEL_NAMES = 2;

// the information passed along an edge, named on it. An edge into or out of the information's own card (an action
// creating an output, or an output realising an outcome) isn't labelled, as the card already names it
const passedOn = (e: AnalysisEdge) => e.informationName && e.informationKey !== e.from && e.informationKey !== e.to ? e.informationName : undefined;
const flowLabel = (names: string[]) => names.length <= MAX_LABEL_NAMES ? names.join(', ')
    : `${names.slice(0, MAX_LABEL_NAMES).join(', ')} and ${names.length - MAX_LABEL_NAMES} more`;
const flowTip = (e: AnalysisEdge) => e.informationName ? `${e.informationName}${e.percentage ? `: ${Math.round(e.percentage)}% of the items` : ''}` : 'Leads to';

// the work flowing between two steps, as one edge however many pieces of information pass between them
const addFlow = (b: Builder, e: AnalysisEdge, colour = FLOW_COLOUR) => {
    if (!b.nodes.has(e.from) || !b.nodes.has(e.to)) return;
    const key = `${e.from}>${e.to}`;
    const name = passedOn(e);
    const names = b.passed.get(key) || [];
    const existing = b.edges.get(key);
    if (existing) {
        existing.weight += e.items || 0;
        if (name && !names.includes(name)) {
            b.passed.set(key, [...names, name]);
            existing.label = flowLabel([...names, name]);
            existing.tooltip += `; ${flowTip(e)}`;
        }
        return;
    }
    if (name) b.passed.set(key, [name]);
    b.edges.set(key, { key, from: e.from, to: e.to, weight: e.items || 0, colour, label: name, tooltip: flowTip(e) });
};

// who does each action and with what: the roles and systems, joined to their actions
const addPerformers = (b: Builder, actions: AnalysisNode[], names: Map<string, string>, focusKey?: string) => {
    const count = new Map<string, number>();
    actions.forEach(a => [...roleKeys(a), ...systemKeys(a)].forEach(k => count.set(k, (count.get(k) || 0) + 1)));
    actions.forEach(a => {
        roleKeys(a).forEach(r => {
            if (!b.nodes.has(r)) b.nodes.set(r, performerCard(r, 'Role', names.get(r) || 'Role', count.get(r) || 0, r === focusKey));
            b.edges.set(`${r}>${a._key}`, { key: `${r}>${a._key}`, from: r, to: a._key, weight: 0, colour: LINK_COLOUR, dashed: true, tooltip: `${names.get(r) || 'The role'} does ${a.name}` });
        });
        systemKeys(a).forEach(s => {
            if (!b.nodes.has(s)) b.nodes.set(s, performerCard(s, 'System', names.get(s) || 'System', count.get(s) || 0, s === focusKey));
            b.edges.set(`${s}>${a._key}`, { key: `${s}>${a._key}`, from: s, to: a._key, weight: 0, colour: LINK_COLOUR, dashed: true, tooltip: `${a.name} uses ${names.get(s) || 'the system'}` });
        });
    });
};

// the largest by hours, to keep it readable
const keepLargest = (nodes: AnalysisNode[], max = MAX_CONTEXT_NODES) => {
    const kept = [...nodes].sort((a, b) => (b.hours || 0) - (a.hours || 0)).slice(0, max);
    return { kept, truncated: Math.max(0, nodes.length - kept.length) };
};

export const buildContextGraph = (graph: ContextGraphData, subject: ContextSubject, names: Map<string, string>, colours: Map<string, string>): ContextGraph | undefined => {
    const byKey = new Map(graph.nodes.map(n => [n._key, n]));
    const incoming = (key: string) => graph.edges.filter(e => e.to === key);
    const outgoing = (key: string) => graph.edges.filter(e => e.from === key);
    const b: Builder = { nodes: new Map(), edges: new Map(), passed: new Map() };
    const flowBetweenKept = () => graph.edges.forEach(e => addFlow(b, e));

    switch (subject.kind) {
        case 'role':
        case 'system': {
            const isRole = subject.kind === 'role';
            const doing = graph.nodes.filter(n => isAction(n) && (isRole ? roleKeys(n) : systemKeys(n)).includes(subject.key));
            if (!doing.length) return undefined;
            const { kept, truncated } = keepLargest(doing);
            kept.forEach(a => b.nodes.set(a._key, card(a, colours, false)));
            addPerformers(b, kept, names, subject.key);
            flowBetweenKept();
            const name = names.get(subject.key) || subject.name || (isRole ? 'The role' : 'The system');
            const hours = doing.reduce((t, a) => t + (a.hours || 0), 0);
            return {
                title: name, subtitle: `${isRole ? 'Role' : 'System'}: the ${plural(doing.length, 'action')} it ${isRole ? 'does' : 'is used in'}, ${hoursText(hours)}, with the work flowing between them`,
                nodes: [...b.nodes.values()], edges: [...b.edges.values()], focus: [subject.key], truncated,
                lens: isRole ? 'people' : 'systems', lensFocus: subject.key,
            };
        }
        case 'action': {
            const action = byKey.get(subject.key);
            if (!action) return undefined;
            // each step once, though it may pass on more than one piece of information
            const before = [...new Set(incoming(action._key).map(e => byKey.get(e.from)).filter((n): n is AnalysisNode => !!n))];
            const after = [...new Set(outgoing(action._key).map(e => byKey.get(e.to)).filter((n): n is AnalysisNode => !!n))];
            const near = keepLargest([...new Set([...before, ...after])].filter(n => n._key !== action._key), MAX_CONTEXT_NODES - 1);
            ;[action, ...near.kept].forEach(n => b.nodes.set(n._key, card(n, colours, n._key === action._key)));
            addPerformers(b, [action], names);
            flowBetweenKept();
            return {
                title: action.name, subtitle: `Action: ${theSteps(before.length)} before it, ${theSteps(after.length)} after it, and who does it with what`,
                nodes: [...b.nodes.values()], edges: [...b.edges.values()], focus: [action._key], truncated: near.truncated, lens: 'actions', lensFocus: action._key,
            };
        }
        case 'output': {
            const output = byKey.get(subject.key);
            if (!output) return undefined;
            // the steps that lead to it, nearest first, until there are as many as can be read
            const seen = new Set([output._key]);
            const queue = [output._key];
            while (queue.length && seen.size < MAX_CONTEXT_NODES) {
                const key = queue.shift() as string;
                incoming(key).forEach(e => {
                    if (!seen.has(e.from) && byKey.has(e.from) && seen.size < MAX_CONTEXT_NODES) { seen.add(e.from); queue.push(e.from); }
                });
            }
            // and the outcomes it realises
            outgoing(output._key).forEach(e => { if (byKey.get(e.to)?.superType === NODE_SUPERTYPE.OUTCOMES) seen.add(e.to); });
            seen.forEach(k => b.nodes.set(k, card(byKey.get(k) as AnalysisNode, colours, k === output._key)));
            flowBetweenKept();
            const steps = [...seen].filter(k => isAction(byKey.get(k) as AnalysisNode)).length;
            return {
                title: output.name, subtitle: `Output: the ${plural(steps, 'step')} that lead to it${queue.length ? ' nearest it' : ''}, and what it goes on to realise`,
                nodes: [...b.nodes.values()], edges: [...b.edges.values()], focus: [output._key], truncated: queue.length, lens: 'information', lensFocus: output._key,
            };
        }
        case 'detour': {
            // a detour the user marked on one action has no split or join: just that action
            const steps = graph.nodes.filter(n => isAction(n) && (subject.joinKey
                ? n.onDetour && n.detourSplitKey === subject.key && n.detourJoinKey === subject.joinKey
                : n._key === subject.key));
            if (!steps.length) return undefined;
            // and the steps either side of it on the main flow, where items leave it and come back (where they leave or
            // return can be a piece of information, which the graph has as an edge rather than a node)
            const inDetour = new Set(steps.map(s => s._key));
            const before = steps.flatMap(s => incoming(s._key)).filter(e => !inDetour.has(e.from))
            const after = steps.flatMap(s => outgoing(s._key)).filter(e => !inDetour.has(e.to))
            const either = [...before.map(e => byKey.get(e.from)), ...after.map(e => byKey.get(e.to))].filter((n): n is AnalysisNode => !!n)
            ;[...steps, ...either].forEach(n => { if (!b.nodes.has(n._key)) b.nodes.set(n._key, card(n, colours, inDetour.has(n._key))); });
            graph.edges.forEach(e => addFlow(b, e, inDetour.has(e.from) || inDetour.has(e.to) ? DETOUR_COLOUR : FLOW_COLOUR));
            const hours = steps.reduce((t, s) => t + (s.hours || 0), 0);
            const share = steps[0].detourShare > 0 ? `, ${shareText(steps[0].detourShare)} of the items` : '';
            // where it leaves and returns, by name: an action's, or the information's on the edge
            const nameOf = (key: string | undefined, edges: AnalysisEdge[]) => key && (byKey.get(key)?.name || edges.find(e => e.informationKey === key)?.informationName)
            const leaves = nameOf(subject.key, before) || 'the main flow'
            const returns = nameOf(subject.joinKey, after) || 'the main flow'
            return {
                title: subject.joinKey ? `Detour from ${leaves}` : steps[0].name,
                subtitle: subject.joinKey
                    ? `Exception work: the ${plural(steps.length, 'step')} items take off the main flow${share}, ${hoursText(hours)}, until they return at ${returns}`
                    : `Exception work: marked as such, ${hoursText(hours)}, with the steps either side of it`,
                nodes: [...b.nodes.values()], edges: [...b.edges.values()], focus: steps.map(s => s._key), truncated: 0, lens: 'actions', lensFocus: steps[0]._key,
            };
        }
    }
};

// what a node of the analysis graph is, as a subject: by its key, or failing that by its name
export const subjectForNode = (graph: ContextGraphData, names: Map<string, string>, keyOrName: string): ContextSubject | undefined => {
    const node = graph.nodes.find(n => n._key === keyOrName) || graph.nodes.find(n => n.name?.trim().toLowerCase() === keyOrName.trim().toLowerCase());
    if (node) return { kind: isAction(node) ? 'action' : 'output', key: node._key, name: node.name };
    // a role or system, which the graph folds into its actions
    const performerKey = names.has(keyOrName) ? keyOrName : [...names.entries()].find(([, n]) => n.trim().toLowerCase() === keyOrName.trim().toLowerCase())?.[0];
    if (!performerKey) return undefined;
    const asRole = graph.nodes.some(n => roleKeys(n).includes(performerKey));
    const asSystem = graph.nodes.some(n => systemKeys(n).includes(performerKey));
    if (asRole) return { kind: 'role', key: performerKey, name: names.get(performerKey) };
    if (asSystem) return { kind: 'system', key: performerKey, name: names.get(performerKey) };
    return undefined;
};

// a detour row's key is "<split>><join>", or the action's key for work the user marked
export const subjectForDetour = (rowKey: string, name?: string): ContextSubject => {
    const [split, join] = rowKey.split('>');
    return join ? { kind: 'detour', key: split, joinKey: join, name } : { kind: 'detour', key: rowKey, name };
};


// what a recommendation is about: a role short of capacity by its key, an automation or integration by its first step,
// or, for one from the EPM scorecard, the first of its subjects found by name
export const subjectForRecommendation = (r: { focus?: string; subjects: string[] }, graph: ContextGraphData, names: Map<string, string>): ContextSubject | undefined => {
    for (const name of [...(r.focus ? [r.focus] : []), ...(r.subjects || [])]) {
        const s = subjectForNode(graph, names, name);
        if (s) return s;
    }
    return undefined;
};
